import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  BarChart3, 
  TrendingUp, 
  Check, 
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  isSameMonth, 
  isSameDay, 
  subMonths,
  addMonths,
  isAfter
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Habit } from '../types';
import { useHabits } from '../contexts/HabitsContext';
import CustomValueChart from './CustomValueChart';
import styles from './HabitHistoryView.module.css';

// Registrar componentes do Chart.js - removido temporariamente

interface HabitHistoryViewProps {
  habit: Habit;
  isOpen: boolean;
  onClose: () => void;
}

interface CompletionData {
  date: string;
  completed: boolean;
  customValue?: string | number;
  notes?: string;
}

const HabitHistoryView: React.FC<HabitHistoryViewProps> = ({ habit, isOpen, onClose }) => {
  const { getHabitHistory, addHistoryCompletion, removeHistoryCompletion, isHabitCompletedToday } = useHabits();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [completionHistory, setCompletionHistory] = useState<CompletionData[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'chart'>('calendar');
  const [showAddModal, setShowAddModal] = useState(false);
  const [customValueInput, setCustomValueInput] = useState<string | number>('');
  const [notesInput, setNotesInput] = useState('');
  const [loading, setLoading] = useState(false);
  const loadHistoryData = useCallback(async () => {
    if (!habit) return;
    
    setLoading(true);
    try {
      // SINCRONIZAR com HabitDetailsModal: usar mesmo período (90 dias até hoje)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90); // 3 meses = ~90 dias
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const history = await getHabitHistory(habit.id, startDateStr, endDateStr);
      setCompletionHistory(history);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  }, [habit, getHabitHistory]); // Removido currentMonth da dependência

  useEffect(() => {
    if (isOpen && habit) {
      loadHistoryData();
    }
  }, [isOpen, habit, loadHistoryData]);

  // Recarregar quando o estado de hoje mudar
  const habitCompletedToday = habit ? isHabitCompletedToday(habit.id) : false;
  useEffect(() => {
    if (isOpen && habit) {
      // Recarregar dados para incluir estado atual de hoje
      loadHistoryData();
    }
  }, [habitCompletedToday, isOpen, habit, loadHistoryData, isHabitCompletedToday]);

  const isDateCompleted = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return completionHistory.some(h => h.date === dateStr && h.completed);
  };

  const getCompletionForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return completionHistory.find(h => h.date === dateStr);
  };

  const handleDateClick = (date: Date) => {
    console.log('🟡 handleDateClick chamado:', {
      clickedDate: date.toISOString(),
      dateFormatted: format(date, 'yyyy-MM-dd'),
      habitId: habit.id,
      habitName: habit.name
    });
    
    setSelectedDate(date);
    const completion = getCompletionForDate(date);
    
    console.log('🔍 DEBUG handleDateClick:', {
      hasCustomValue: habit.hasCustomValue,
      completion,
      willToggle: !habit.hasCustomValue
    });
    
    if (habit.hasCustomValue) {
      setCustomValueInput(completion?.customValue || '');
      setNotesInput(completion?.notes || '');
      setShowAddModal(true);
    } else {
      // Para hábitos simples, toggle da conclusão
      console.log('🟡 Chamando toggleCompletion para data:', format(date, 'yyyy-MM-dd'));
      toggleCompletion(date);
    }
  };

  const toggleCompletion = async (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isCompleted = isDateCompleted(date);
    
    try {
      console.log('🟡 Toggling completion for date:', dateStr, 'isCompleted:', isCompleted);
      console.log('🔍 DEBUG toggleCompletion:', {
        clickedDate: date.toISOString(),
        dateStr,
        isCompleted,
        habitId: habit.id,
        habitName: habit.name
      });
      
      if (isCompleted) {
        console.log('🟡 Chamando removeHistoryCompletion para:', habit.id, dateStr);
        await removeHistoryCompletion(habit.id, dateStr);
      } else {
        console.log('🟡 Chamando addHistoryCompletion para:', habit.id, dateStr);
        await addHistoryCompletion(habit.id, dateStr, {
          customValue: undefined,
          notes: ''
        });
      }
      
      // Atualizar o estado local após a operação
      setCompletionHistory(prev => {
        return prev.map(h => 
          h.date === dateStr 
            ? { ...h, completed: !isCompleted }
            : h
        );
      });
      
      console.log('✅ Completion toggled successfully');
      
    } catch (error) {
      console.error('❌ Erro ao atualizar conclusão:', error);
    }
  };

  const handleSaveCustomValue = async () => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    try {
      console.log('🟡 Saving custom value for date:', dateStr);
      
      await addHistoryCompletion(habit.id, dateStr, {
        customValue: customValueInput || undefined,
        notes: notesInput.trim() || undefined
      });
      
      // Atualizar o estado local após a operação
      setCompletionHistory(prev => {
        const existingIndex = prev.findIndex(h => h.date === dateStr);
        const newEntry = {
          date: dateStr,
          completed: true,
          customValue: customValueInput || undefined,
          notes: notesInput.trim() || undefined
        };
        
        if (existingIndex >= 0) {
          // Atualizar entry existente
          const updated = [...prev];
          updated[existingIndex] = newEntry;
          return updated;
        } else {
          // Adicionar nova entry
          return [...prev, newEntry];
        }
      });
      
      setShowAddModal(false);
      setCustomValueInput('');
      setNotesInput('');
      
      console.log('✅ Custom value saved successfully');
      
    } catch (error) {
      console.error('❌ Erro ao salvar valor personalizado:', error);
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];
    
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayFormatted = format(day, dateFormat);
        const cloneDay = day;
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isToday = isSameDay(day, new Date());
        const isCompleted = isDateCompleted(day);
        const completion = getCompletionForDate(day);
        const isFuture = isAfter(day, new Date());

        days.push(
          <div
            className={`${styles.calendarDay} ${
              !isCurrentMonth ? styles.otherMonth : ''
            } ${isToday ? styles.today : ''} ${
              isCompleted ? styles.completed : ''
            } ${isFuture ? styles.future : ''}`}
            key={day.toString()}
            onClick={() => !isFuture && handleDateClick(cloneDay)}
          >
            <span className={styles.dayNumber}>{dayFormatted}</span>
            {isCompleted && (
              <div className={styles.completionIndicator}>
                {habit.hasCustomValue && completion?.customValue ? (                  habit.customValueType === 'text' && 
                  (completion.customValue as string).length <= 2 ? (
                    <span className={styles.emoji}>{completion.customValue}</span>
                  ) : (
                    <FileText className={styles.textIcon} />
                  )
                ) : (
                  <Check className={styles.checkIcon} />
                )}
              </div>
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className={styles.calendarRow} key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }

    return (
      <div className={styles.calendar}>
        <div className={styles.calendarHeader}>
          <button 
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className={styles.navButton}
          >
            <ChevronLeft />
          </button>
          <h3 className={styles.monthTitle}>
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
          </h3>
          <button 
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className={styles.navButton}
          >
            <ChevronRight />
          </button>
        </div>
        
        <div className={styles.weekdays}>
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className={styles.weekday}>{day}</div>
          ))}
        </div>
        
        <div className={styles.calendarGrid}>
          {rows}
        </div>
      </div>
    );
  };  const renderChart = () => {
    if (!habit.hasCustomValue || habit.customValueType !== 'number') {
      return (
        <div className={styles.chartPlaceholder}>
          <BarChart3 className={styles.placeholderIcon} />
          <p>Gráficos disponíveis apenas para hábitos com valores numéricos</p>
        </div>
      );
    }

    return (
      <div className={styles.chartContainer}>
        <CustomValueChart
          data={completionHistory}
          customValueLabel={habit.customValueLabel}
          customValueUnit={habit.customValueUnit}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className={styles.backdrop} onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={styles.modal}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.header}>
            <h2 className={styles.title}>Histórico - {habit.name}</h2>
            <button onClick={onClose} className={styles.closeButton}>
              ×
            </button>
          </div>

          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${viewMode === 'calendar' ? styles.active : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              <CalendarIcon className={styles.tabIcon} />
              Calendário
            </button>
            {habit.hasCustomValue && habit.customValueType === 'number' && (
              <button
                className={`${styles.tab} ${viewMode === 'chart' ? styles.active : ''}`}
                onClick={() => setViewMode('chart')}
              >
                <TrendingUp className={styles.tabIcon} />
                Gráfico
              </button>
            )}
          </div>

          <div className={styles.content}>
            {loading ? (
              <div className={styles.loading}>Carregando histórico...</div>
            ) : viewMode === 'calendar' ? (
              renderCalendar()
            ) : (
              renderChart()
            )}
          </div>

          {/* Modal para valores personalizados */}
          <AnimatePresence>
            {showAddModal && (
              <div className={styles.modalOverlay}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={styles.addModal}
                >
                  <h3>
                    {selectedDate && format(selectedDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </h3>
                  
                  {habit.hasCustomValue && (
                    <div className={styles.inputGroup}>
                      <label>{habit.customValueLabel || 'Valor'}</label>
                      {habit.customValueType === 'number' ? (
                        <input
                          type="number"
                          value={customValueInput}
                          onChange={(e) => setCustomValueInput(Number(e.target.value))}
                          placeholder={`Ex: 30 ${habit.customValueUnit || ''}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={customValueInput}
                          onChange={(e) => setCustomValueInput(e.target.value)}
                          placeholder="Ex: 😊 ou texto livre"
                        />
                      )}
                    </div>
                  )}

                  <div className={styles.inputGroup}>
                    <label>Observações (opcional)</label>
                    <textarea
                      value={notesInput}
                      onChange={(e) => setNotesInput(e.target.value)}
                      placeholder="Adicione suas observações..."
                      rows={3}
                    />
                  </div>

                  <div className={styles.modalActions}>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className={styles.cancelButton}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveCustomValue}
                      className={styles.saveButton}
                    >
                      Salvar
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default HabitHistoryView;
